import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { RefreshCw, Trash2, Clock, Loader2 } from "lucide-react";
import { useState } from "react";

interface ExpiredItem {
  itemId: string;
  productName: string;
  hotelName?: string;
}

interface CartExpirationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  expiredItems: ExpiredItem[];
  onRevalidate: (itemIds: string[]) => Promise<void>;
  onRemove: (itemIds: string[]) => void;
}

export const CartExpirationDialog = ({
  open,
  onOpenChange,
  expiredItems,
  onRevalidate,
  onRemove,
}: CartExpirationDialogProps) => {
  const { t } = useTranslation();
  const [isRevalidating, setIsRevalidating] = useState(false);

  if (expiredItems.length === 0) return null;

  const handleRevalidateAll = async () => {
    setIsRevalidating(true);
    try {
      await onRevalidate(expiredItems.map(i => i.itemId));
      onOpenChange(false);
    } finally {
      setIsRevalidating(false);
    }
  };

  const handleRemoveAll = () => {
    onRemove(expiredItems.map(i => i.itemId));
    onOpenChange(false);
  };

  const isSingleItem = expiredItems.length === 1;
  const itemName = isSingleItem 
    ? expiredItems[0].hotelName || expiredItems[0].productName
    : null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 mb-2">
            <Clock className="h-5 w-5" />
            <span className="font-semibold">{t('cart.itemsExpired')}</span>
          </div>
          <AlertDialogTitle>
            {isSingleItem 
              ? t('cart.singleItemExpiredTitle')
              : t('cart.multipleItemsExpiredTitle', { count: expiredItems.length })}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            {isSingleItem ? (
              <p>
                {t('cart.singleItemExpiredDescription', { name: itemName })}
              </p>
            ) : (
              <>
                <p>{t('cart.multipleItemsExpiredDescription')}</p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {expiredItems.map(item => (
                    <li key={item.itemId} className="text-foreground">
                      {item.hotelName || item.productName}
                    </li>
                  ))}
                </ul>
              </>
            )}
            <p className="text-sm text-muted-foreground">
              {t('cart.expiredItemsAction')}
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleRemoveAll}
            disabled={isRevalidating}
            className="w-full sm:w-auto"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {isSingleItem ? t('cart.removeItem') : t('cart.removeAllItems')}
          </Button>
          <Button
            onClick={handleRevalidateAll}
            disabled={isRevalidating}
            className="w-full sm:w-auto"
          >
            {isRevalidating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('cart.revalidating')}
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                {isSingleItem ? t('cart.revalidateItem') : t('cart.revalidateAllItems')}
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
